import type { ReactNode } from 'react';

interface MobileRecordCardProps {
  title: string;
  subtitle?: string;
  rows: Array<{ label: string; value: string | ReactNode; highlight?: boolean; color?: 'emerald' | 'red' | 'default' }>;
  badges?: Array<{ text: string; className: string; icon?: string }>;
  onClick?: () => void;
  actionLabel?: string;
  onAction?: () => void;
}

export default function MobileRecordCard({
  title,
  subtitle,
  rows,
  badges,
  onClick,
  actionLabel,
  onAction,
}: MobileRecordCardProps) {
  const getRowColor = (color?: string) => {
    switch (color) {
      case 'emerald': return 'text-emerald-600';
      case 'red': return 'text-red-500';
      default: return 'text-foreground-700';
    }
  };

  return (
    <div
      className={`bg-background-50 rounded-xl border border-background-200/70 p-3.5 ${onClick ? 'cursor-pointer active:scale-[0.98] transition-transform' : ''}`}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground-800">{title}</p>
          {subtitle && <p className="text-xs text-foreground-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>

      {/* Badges */}
      {badges && badges.length > 0 && (
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          {badges.map((badge, i) => (
            <span key={i} className={badge.className}>
              {badge.icon && <i className={`${badge.icon} text-[11px]`}></i>}
              {badge.text}
            </span>
          ))}
        </div>
      )}

      {/* Data Rows */}
      <div className="space-y-1.5">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-xs text-foreground-400">{row.label}</span>
            <span className={`text-xs font-medium ${row.highlight ? getRowColor(row.color) : 'text-foreground-700'}`}>
              {row.value}
            </span>
          </div>
        ))}
      </div>

      {/* Action */}
      {actionLabel && onAction && (
        <button
          onClick={(e) => { e.stopPropagation(); onAction(); }}
          className="mt-3 w-full text-center py-2 rounded-lg bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 active:scale-[0.98] transition-all"
          style={{ minHeight: 40 }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}